"use client"

import { useChainId } from "wagmi"
import { useMemo } from "react"
import { usePimlicoGaslessWallet } from "./usePimlicoGaslessWallet"
import { useDynamicGasSponsorship } from "./useDynamicGasSponsorship"
import {
  isGasSponsorshipSupported,
  getChainGasSponsorshipConfig,
  getProviderConfig,
  GAS_SPONSORSHIP_GLOBAL_CONFIG,
} from "./config/gasSponsorship"

/**
 * Hook that provides chain-specific wallet functionality
 * - Supported chains: Uses gas sponsorship based on centralized configuration
 * - Other chains: Uses native account abstraction where available
 *
 * This implementation uses a centralized config to determine which chains
 * support gas sponsorship and what providers/styles to use.
 */
export const useChainSpecificWallet = () => {
  const chainId = useChainId()

  // Initialize hooks (always call them to avoid conditional hooks)
  const pimlicoGasless = usePimlicoGaslessWallet()
  const dynamicGasSponsorship = useDynamicGasSponsorship()

  // Memoize configuration lookups to avoid recalculation on every render
  const chainConfig = useMemo(
    () => getChainGasSponsorshipConfig(chainId),
    [chainId]
  )
  const providerConfig = useMemo(() => getProviderConfig(chainId), [chainId])
  const isSupported = useMemo(
    () => isGasSponsorshipSupported(chainId),
    [chainId]
  )

  // Memoize safe defaults to avoid recreating functions on every render
  const safeDefaults = useMemo(
    () => ({
      isReady: false,
      isInitializing: false,
      error: null,
      hasGasSponsorship: false,
      executeGaslessTransaction: async () => {
        throw new Error(
          `Gasless transactions not available on chain ${chainId}`
        )
      },
      getGasSponsorshipInfo: async () => {
        throw new Error(`Gas sponsorship not available on chain ${chainId}`)
      },
      reinitialize: () => {},
    }),
    [chainId]
  )

  try {
    // Check if gas sponsorship is supported for current chain
    if (isSupported && chainConfig && providerConfig) {
      // Use appropriate gas sponsorship implementation based on provider/style
      if (providerConfig.style === "azuro-style-pimlico") {
        if (dynamicGasSponsorship.isReady) {
          return {
            chainId,
            chainName: chainConfig.chainName,
            gaslessType: providerConfig.style,
            walletType: "smart",
            gasSponsorship: {
              enabled: true,
              provider: providerConfig.provider,
              supportedChains: [chainId],
              style: providerConfig.style,
              features: chainConfig.features,
            },
            // Use dynamic gas sponsorship implementation
            isReady: dynamicGasSponsorship.isReady,
            isInitializing: dynamicGasSponsorship.isInitializing,
            error: dynamicGasSponsorship.error,
            hasGasSponsorship: dynamicGasSponsorship.hasGasSponsorship,
            canSponsorGas: dynamicGasSponsorship.canSponsorGas,
            executeGaslessTransaction:
              dynamicGasSponsorship.executeGaslessTransaction,
            getGasSponsorshipInfo: dynamicGasSponsorship.getGasSponsorshipInfo,
            executeFallbackTransaction:
              dynamicGasSponsorship.executeFallbackTransaction,
            address: dynamicGasSponsorship.smartWalletAddress || undefined,
            smartWalletAddress: dynamicGasSponsorship.smartWalletAddress,
            safeSmartWalletAddress: dynamicGasSponsorship.smartWalletAddress,
            provider: dynamicGasSponsorship.provider,
            style: dynamicGasSponsorship.style,
            features: dynamicGasSponsorship.features,
            smartAccountClient: dynamicGasSponsorship.smartAccountClient,
            debugInfo: dynamicGasSponsorship.debugInfo,
          }
        } else {
          // Show debug info about why gas sponsorship isn't ready
          return {
            chainId,
            chainName: chainConfig.chainName,
            gaslessType: "none",
            walletType: "debugging",
            gasSponsorship: {
              enabled: false,
              provider: null,
              supportedChains: [],
              features: chainConfig.features,
            },
            address: undefined,
            ...safeDefaults,
            error: `Gas sponsorship not ready for ${chainConfig.chainName}: ${dynamicGasSponsorship.error}`,
            debugInfo: {
              chainConfig,
              providerConfig,
              gasSponsorship: {
                isReady: dynamicGasSponsorship.isReady,
                isInitializing: dynamicGasSponsorship.isInitializing,
                error: dynamicGasSponsorship.error,
                smartWalletAddress: dynamicGasSponsorship.smartWalletAddress,
                hasGasSponsorship: dynamicGasSponsorship.hasGasSponsorship,
                canSponsorGas: dynamicGasSponsorship.canSponsorGas,
                debugInfo: dynamicGasSponsorship.debugInfo,
              },
            },
          }
        }
      } else if (providerConfig.style === "native-aa") {
        // Use Pimlico gasless wallet for native AA
        return {
          chainId,
          chainName: chainConfig.chainName,
          gaslessType: providerConfig.style,
          walletType: "native",
          gasSponsorship: {
            enabled: GAS_SPONSORSHIP_GLOBAL_CONFIG.enabled,
            provider: providerConfig.provider,
            supportedChains: [chainId],
            style: providerConfig.style,
            features: chainConfig.features,
          },
          ...pimlicoGasless,
          address: pimlicoGasless.smartAccountAddress || undefined,
        }
      } else {
        // Other provider styles (alchemy-aa, biconomy-aa, etc.)
        return {
          chainId,
          chainName: chainConfig.chainName,
          gaslessType: "none",
          walletType: "unsupported",
          gasSponsorship: {
            enabled: false,
            provider: providerConfig.provider,
            supportedChains: [],
            features: chainConfig.features,
          },
          address: undefined,
          ...safeDefaults,
          error: `Provider style ${providerConfig.style} not yet implemented`,
        }
      }
    } else {
      // Chain not supported for gas sponsorship - use native account abstraction
      return {
        chainId,
        chainName: chainConfig?.chainName || `Chain ${chainId}`,
        gaslessType: "native-aa",
        walletType: "native",
        gasSponsorship: {
          enabled: false,
          provider: null,
          supportedChains: [],
          features: {
            sponsorApprovals: false,
            sponsorBets: false,
            sponsorSwaps: false,
            sponsorRedeems: false,
            sponsorWithdraws: false,
            sponsorAll: false,
          },
        },
        // Use Pimlico gasless wallet for other chains
        ...pimlicoGasless,
        address: pimlicoGasless.smartAccountAddress || undefined,
      }
    }
  } catch (error) {
    return {
      chainId,
      chainName: chainConfig?.chainName || "Error",
      gaslessType: "none",
      walletType: "error",
      gasSponsorship: {
        enabled: false,
        provider: null,
        supportedChains: [],
        features: {
          sponsorApprovals: false,
          sponsorBets: false,
          sponsorSwaps: false,
          sponsorRedeems: false,
          sponsorWithdraws: false,
          sponsorAll: false,
        },
      },
      address: undefined,
      ...safeDefaults,
      error: error instanceof Error ? error.message : "Unknown error",
    }
  }
}
